/**
 * Utility for exporting printable student Raw Data response sheets to PDF
 * Compatible with html2pdf.js bundle
 */

function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseAnswers(rawAnswers) {
  if (!rawAnswers) return {};
  if (typeof rawAnswers === 'object' && !Array.isArray(rawAnswers)) {
    return rawAnswers;
  }
  if (typeof rawAnswers === 'string') {
    try {
      const parsed = JSON.parse(rawAnswers);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
}

const RATING_DESCRIPTIONS = {
  1: 'Needs Improvement',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent'
};

export async function downloadRawFeedbackPdf({
  feedbacks = [],
  reportType = 'lms', // 'lms' | 'platform'
  universityName = 'All',
  courseName = 'All',
  courseForms = [],
  platformForms = [],
  lmsSchema = null
}) {
  if (!feedbacks || feedbacks.length === 0) {
    alert('No feedback submissions available to export for the selected filter.');
    return;
  }

  // Ensure html2pdf.js is loaded
  if (!window.html2pdf) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load html2pdf library.'));
      document.head.appendChild(script);
    });
  }

  // Create temporary container for report rendering.
  // CRITICAL: The container itself must NOT be position:absolute/fixed — html2pdf clones
  // this node into its own height:auto wrapper, and an out-of-flow clone collapses that
  // wrapper to 0 height, producing a blank canvas/PDF. Offscreen hiding is handled by a
  // wrapper parent instead (html2pdf only clones the container node, not its parent).
  const container = document.createElement('div');
  container.id = 'raw-feedback-pdf-render-root';
  // Match html2pdf's inner page area exactly (A4 210mm minus 10mm margins = 190mm).
  // A wider container overflows that area and gets its right edge clipped in the PDF.
  container.style.width = '190mm';
  container.style.maxWidth = '190mm';
  container.style.padding = '24px 28px';
  container.style.background = '#ffffff';
  container.style.color = '#0f172a';
  container.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  container.style.fontSize = '12px';
  container.style.lineHeight = '1.5';
  container.style.boxSizing = 'border-box';

  const renderWrapper = document.createElement('div');
  renderWrapper.id = 'raw-feedback-pdf-render-wrapper';
  renderWrapper.style.position = 'absolute';
  renderWrapper.style.left = '-10000px';
  renderWrapper.style.top = '0';
  renderWrapper.style.pointerEvents = 'none';
  renderWrapper.appendChild(container);

  const isLms = reportType === 'lms';
  const reportTitle = isLms
    ? 'LMS Course Feedback — Raw Data Evaluation Report'
    : 'Platform Experience & Feedback — Raw Data Report';
  const subtitle = isLms
    ? 'Detailed Student Response Sheets, Module Evaluations & Subject Feedback'
    : 'Detailed Student Response Sheets & Overall Platform Experience Surveys';

  const uniDisplay = universityName && universityName !== 'All' ? universityName : 'All Participating Colleges';
  const courseDisplay = isLms
    ? (courseName && courseName !== 'All' ? courseName : 'All LMS Courses')
    : 'Platform Usability & Experience Evaluation';

  const now = new Date();
  const dateFormatted = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' at ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  // Pre-calculate summary statistics
  const uniqueUniversities = Array.from(new Set(feedbacks.map(f => f.university_name).filter(Boolean)));
  const uniqueBatches = Array.from(new Set(feedbacks.map(f => f.batch_name).filter(Boolean)));
  
  // Build a master question lookup dictionary across all available form definitions
  const masterQuestionsDict = {};
  [...(courseForms || []), ...(platformForms || [])].forEach(form => {
    (form.questions || []).forEach(q => {
      if (q && q.id && !masterQuestionsDict[q.id]) {
        masterQuestionsDict[q.id] = q;
      }
    });
  });

  if (lmsSchema) {
    ['module_feedback', 'platform_feedback'].forEach(sec => {
      const secQs = lmsSchema[sec];
      if (Array.isArray(secQs)) {
        secQs.forEach(q => {
          if (q && q.id && !masterQuestionsDict[q.id]) {
            masterQuestionsDict[q.id] = q;
          }
        });
      }
    });
  }

  // Build report HTML
  let html = `
    <!-- Report Top Header Banner -->
    <div style="margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 16px;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
        <div style="max-width: 540px;">
          <div style="display: inline-block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; background: #eff6ff; color: #1d4ed8; padding: 2px 8px; border-radius: 4px; margin-bottom: 6px;">
            ${isLms ? 'LMS Course Evaluation' : 'Platform System Survey'}
          </div>
          <h1 style="margin: 0 0 4px 0; font-size: 20px; font-weight: 800; color: #1e3a8a; letter-spacing: -0.02em; line-height: 1.25;">
            ${escapeHtml(reportTitle)}
          </h1>
          <p style="margin: 0; font-size: 12px; color: #64748b; line-height: 1.4;">
            ${escapeHtml(subtitle)}
          </p>
        </div>
        <div style="text-align: right; font-size: 11px; color: #64748b; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px;">
          <div><strong style="color: #0f172a;">Generated:</strong> ${escapeHtml(dateFormatted)}</div>
          <div><strong style="color: #0f172a;">Total Submissions:</strong> <span style="color: #2563eb; font-weight: 700;">${feedbacks.length}</span></div>
        </div>
      </div>

      <!-- Scope Filter Cards -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; font-size: 11.5px;">
        <div>
          <span style="color: #64748b; font-weight: 500;">College / University Scope:</span>
          <div style="color: #0f172a; font-weight: 700; margin-top: 2px; font-size: 12.5px;">${escapeHtml(uniDisplay)}</div>
        </div>
        <div>
          <span style="color: #64748b; font-weight: 500;">Target Course / Form Scope:</span>
          <div style="color: #0f172a; font-weight: 700; margin-top: 2px; font-size: 12.5px;">${escapeHtml(courseDisplay)}</div>
        </div>
      </div>

      <!-- Executive KPI Strip -->
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 10px;">
        <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; text-align: center;">
          <div style="font-size: 16px; font-weight: 800; color: #2563eb;">${feedbacks.length}</div>
          <div style="font-size: 10.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em;">Student Submissions</div>
        </div>
        <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; text-align: center;">
          <div style="font-size: 16px; font-weight: 800; color: #0f172a;">${uniqueUniversities.length || 1}</div>
          <div style="font-size: 10.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em;">Colleges Represented</div>
        </div>
        <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; text-align: center;">
          <div style="font-size: 16px; font-weight: 800; color: #0f172a;">${uniqueBatches.length || 1}</div>
          <div style="font-size: 10.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em;">Academic Batches</div>
        </div>
      </div>
    </div>

    <!-- Student Response Sheets Section -->
    <div style="display: flex; flex-direction: column; gap: 16px;">
  `;

  // Render individual student response sheets
  feedbacks.forEach((item, index) => {
    const studentName = item.student_name || `Student #${item.student_id}`;
    const uniName = item.university_name || 'Unknown College';
    const batchName = item.batch_name || 'General Batch';
    const courseOrForm = isLms
      ? (item.course_name || 'Course')
      : (item.form_title ? `Form: ${item.form_title}` : (item.course_name || 'Platform Survey'));
    const submissionDate = item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : '—';
    const usn = item.usn || '';
    const email = item.student_email || '';

    // Parse answers robustly (handle stringified JSON, null, or fallback between module & platform)
    let answersObj = isLms ? parseAnswers(item.module_feedback) : parseAnswers(item.platform_feedback);
    
    // Fallback: If primary target section is empty, inspect alternate section to prevent showing blank data
    if (Object.keys(answersObj).length === 0) {
      const altObj = isLms ? parseAnswers(item.platform_feedback) : parseAnswers(item.module_feedback);
      if (Object.keys(altObj).length > 0) {
        answersObj = altObj;
      }
    }

    // Resolve question schema for this item
    let formQuestions = [];
    if (isLms) {
      const matched = (courseForms || []).find(cf => cf.id === (item.form_id || item.course_id));
      formQuestions = matched?.questions || courseForms[0]?.questions || lmsSchema?.module_feedback || [];
    } else {
      const matched = (platformForms || []).find(pf => pf.id === (item.form_id || item.course_id));
      formQuestions = matched?.questions || platformForms[0]?.questions || lmsSchema?.platform_feedback || [];
    }

    // Build ordered list of questions and answers
    const answeredMap = new Map();

    // 1. First add questions defined in the matched form/schema that have answers
    (formQuestions || []).forEach(q => {
      if (!q || !q.id) return;
      const val = answersObj[q.id];
      if (val !== undefined && val !== null && val !== '') {
        answeredMap.set(q.id, {
          id: q.id,
          text: q.text || q.question || q.id,
          type: q.type || 'text',
          answer: val
        });
      }
    });

    // 2. Next, check master dictionary for any keys in answersObj
    Object.keys(answersObj).forEach(key => {
      if (!answeredMap.has(key)) {
        const val = answersObj[key];
        if (val !== undefined && val !== null && val !== '') {
          const masterQ = masterQuestionsDict[key];
          answeredMap.set(key, {
            id: key,
            text: masterQ?.text || masterQ?.question || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            type: masterQ?.type || 'text',
            answer: val
          });
        }
      }
    });

    const answeredList = Array.from(answeredMap.values());

    html += `
      <div class="raw-student-sheet" style="page-break-inside: avoid; break-inside: avoid; border: 1.5px solid #cbd5e1; border-radius: 8px; background: #ffffff; padding: 14px 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin-bottom: 14px;">
        <!-- Student Header Strip -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-bottom: 10px;">
          <div>
            <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
              <span style="display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: #eff6ff; color: #2563eb; font-weight: 800; font-size: 11px;">
                ${index + 1}
              </span>
              <span style="font-size: 14px; font-weight: 700; color: #0f172a;">${escapeHtml(studentName)}</span>
              ${usn ? `<span style="font-size: 11px; background: #f1f5f9; color: #334155; padding: 2px 7px; border-radius: 4px; font-family: monospace; font-weight: 600;">${escapeHtml(usn)}</span>` : ''}
            </div>
            <div style="font-size: 11px; color: #64748b; margin-top: 3px;">
              <strong style="color: #1e293b;">${escapeHtml(uniName)}</strong>
              ${email ? ` • <span style="font-family: monospace; color: #475569;">${escapeHtml(email)}</span>` : ''}
              <span> • Batch: <strong style="color: #0f172a;">${escapeHtml(batchName)}</strong></span>
            </div>
          </div>
          <div style="text-align: right; font-size: 11px; color: #64748b; white-space: nowrap;">
            <div style="font-weight: 700; color: #2563eb;">${escapeHtml(courseOrForm)}</div>
            <div>Submitted: <strong style="color: #0f172a;">${escapeHtml(submissionDate)}</strong></div>
          </div>
        </div>

        <!-- Answers List -->
        <div style="display: flex; flex-direction: column; gap: 8px;">
    `;

    if (answeredList.length === 0) {
      html += `
        <div style="font-size: 11.5px; color: #94a3b8; font-style: italic; padding: 10px 12px; background: #f8fafc; border: 1px dashed #e2e8f0; border-radius: 6px; text-align: center;">
          No survey answers recorded for this student submission.
        </div>
      `;
    } else {
      answeredList.forEach((qItem, qIdx) => {
        const val = qItem.answer;
        let answerHtml = '';

        // Check if numeric rating
        const numVal = Number(val);
        const isRating = (qItem.type === 'rating' || (typeof val === 'number' && val >= 1 && val <= 5) || (typeof val === 'string' && val.length === 1 && !isNaN(numVal) && numVal >= 1 && numVal <= 5));

        if (isRating && numVal >= 1 && numVal <= 5) {
          const roundedRating = Math.round(numVal);
          const stars = '★'.repeat(roundedRating) + '☆'.repeat(5 - roundedRating);
          const desc = RATING_DESCRIPTIONS[roundedRating] || '';
          answerHtml = `
            <div style="display: inline-flex; align-items: center; gap: 8px; background: #fffbeb; border: 1.5px solid #fde68a; color: #b45309; padding: 3px 10px; border-radius: 12px; font-weight: 700; font-size: 11.5px;">
              <span style="color: #f59e0b; font-size: 13px; letter-spacing: 1.5px;">${stars}</span>
              <span>${roundedRating} / 5${desc ? ` (${desc})` : ''}</span>
            </div>
          `;
        } else if (Array.isArray(val)) {
          // Multiple choices
          const tags = val.map(v => `<span style="display: inline-block; background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; margin-right: 4px; margin-bottom: 2px;">${escapeHtml(String(v))}</span>`).join('');
          answerHtml = `<div style="margin-top: 2px;">${tags}</div>`;
        } else if (typeof val === 'string' && val.length > 50) {
          // Longer open-ended response / comment
          answerHtml = `
            <div style="background: #f8fafc; border-left: 3px solid #3b82f6; border: 1px solid #e2e8f0; border-left-width: 3px; padding: 6px 10px; border-radius: 4px; font-size: 11.5px; color: #1e293b; line-height: 1.45; font-style: italic; margin-top: 2px;">
              "${escapeHtml(val)}"
            </div>
          `;
        } else if (typeof val === 'boolean') {
          const boolText = val ? 'Yes' : 'No';
          const bg = val ? '#ecfdf5' : '#fef2f2';
          const border = val ? '#a7f3d0' : '#fecaca';
          const text = val ? '#065f46' : '#991b1b';
          answerHtml = `
            <span style="display: inline-block; background: ${bg}; border: 1px solid ${border}; color: ${text}; padding: 2px 8px; border-radius: 4px; font-weight: 700; font-size: 11px;">
              ${boolText}
            </span>
          `;
        } else {
          // Standard short choice or text
          answerHtml = `
            <div style="display: inline-block; background: #f1f5f9; border: 1px solid #e2e8f0; color: #0f172a; padding: 3px 9px; border-radius: 4px; font-weight: 600; font-size: 11.5px; margin-top: 2px;">
              ${escapeHtml(String(val))}
            </div>
          `;
        }

        html += `
          <div style="border-bottom: 1px dashed #f1f5f9; padding-bottom: 6px;">
            <div style="font-size: 11.5px; font-weight: 600; color: #334155; margin-bottom: 3px;">
              Q${qIdx + 1}. ${escapeHtml(qItem.text)}
            </div>
            <div>
              ${answerHtml}
            </div>
          </div>
        `;
      });
    }

    html += `
        </div>
      </div>
    `;
  });

  html += `
    </div>
    
    <!-- Footer Note -->
    <div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 10.5px; color: #94a3b8;">
      EdvergenceX.Feedback Report
    </div>
  `;

  container.innerHTML = html;
  document.body.appendChild(renderWrapper);

  try {
    // Give browser brief time to layout elements and compute metrics
    await new Promise(resolve => setTimeout(resolve, 150));

    const uniSlug = universityName && universityName !== 'All'
      ? `_${String(universityName).replace(/[^a-zA-Z0-9_-]/g, '_')}`
      : '_All_Colleges';
    const courseSlug = isLms && courseName && courseName !== 'All'
      ? `_${String(courseName).replace(/[^a-zA-Z0-9_-]/g, '_')}`
      : '';
    const filename = isLms
      ? `LMS_Raw_Feedback_Report${uniSlug}${courseSlug}.pdf`
      : `Platform_Raw_Feedback_Report${uniSlug}.pdf`;

    const opt = {
      margin: [10, 10, 10, 10], // 10mm margins all around
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        scrollY: 0,
        scrollX: 0
        // Do NOT set windowWidth here: when it differs from the real viewport,
        // html2canvas lays out at the fake width but subtracts the live-viewport
        // element offset, shifting all content left and clipping it.
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    await window.html2pdf().set(opt).from(container).save();
  } finally {
    if (renderWrapper && renderWrapper.parentNode) {
      renderWrapper.parentNode.removeChild(renderWrapper);
    }
  }
}
